/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-11
 */

import {
  Box,
  Typography,
  Avatar,
  Chip,
  Paper,
  Button,
  Divider,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PersonOutline as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Star as OwnerIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Language as WebsiteIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  CalendarToday as CalendarIcon,
  Verified as VerifiedIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import { StudioRole } from '../../../types/studio';
import { MemberStatus, StudioMember } from '../../../types/studioMembers';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import { useGetStudioMemberDetailQuery } from '../../../features/studios/studiosApi';
import Header from '../../layout/Header';
import { formatLongDate } from '../../../utils/dateLocalization';

interface FieldVisibility {
  showEmail: boolean;
  showPhone: boolean;
  showAddress: boolean;
  showSocialLinks: boolean;
}

const MemberDetailPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { studioId, memberId } = useParams<{
    studioId: string;
    memberId: string;
  }>();
  const { role: currentUserRole } = useStudioPermissions();

  // Fetch member data using the detailed member endpoint
  const { data: member, isLoading } = useGetStudioMemberDetailQuery({
    studioId: studioId || '',
    memberId: memberId || '',
  });

  const getFieldVisibility = (targetMember: StudioMember): FieldVisibility => {
    const profile = (targetMember as any)?.profile;

    // If profile data is not available, fall back to role-based logic
    if (!profile) {
      const isOwnerOrManager =
        currentUserRole && ['owner', 'manager'].includes(currentUserRole);
      return {
        showEmail: isOwnerOrManager || false,
        showPhone: isOwnerOrManager || false,
        showAddress: isOwnerOrManager || false,
        showSocialLinks: isOwnerOrManager || false,
      };
    }

    // Use actual privacy settings from backend
    return {
      showEmail: profile.email_visible || false,
      showPhone: profile.phone_visible || false,
      showAddress: profile.address_visible || false,
      showSocialLinks: true, // Social links are visible if they have values (no specific privacy setting for them)
    };
  };

  const getRoleIcon = (role: StudioRole) => {
    switch (role) {
      case StudioRole.OWNER:
        return <OwnerIcon sx={{ color: '#FFD700' }} />;
      case StudioRole.MANAGER:
        return <ManagerIcon sx={{ color: '#0d47a1' }} />;
      case StudioRole.ADMIN:
        return <AdminIcon sx={{ color: '#1976d2' }} />;
      case StudioRole.MEMBER:
        return <PersonIcon sx={{ color: '#666' }} />;
      default:
        return <PersonIcon sx={{ color: '#666' }} />;
    }
  };

  const getRoleColor = (role: StudioRole) => {
    switch (role) {
      case StudioRole.OWNER:
        return '#FFD700';
      case StudioRole.MANAGER:
        return '#0d47a1';
      case StudioRole.ADMIN:
        return '#1976d2';
      case StudioRole.MEMBER:
        return '#666';
      default:
        return '#666';
    }
  };

  const getStatusColor = (status: MemberStatus) => {
    switch (status) {
      case MemberStatus.ACTIVE:
        return 'success';
      case MemberStatus.INACTIVE:
        return 'default';
      case MemberStatus.PENDING:
        return 'warning';
      case MemberStatus.SUSPENDED:
        return 'error';
      default:
        return 'default';
    }
  };

  const renderPrivateField = (
    value: string,
    isVisible: boolean,
    icon: React.ReactNode,
    label: string
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {icon}
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {isVisible && value ? (
          <Typography variant="body1">{value}</Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LockIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.disabled">
              Private
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderSocialLink = (
    url: string,
    icon: React.ReactNode,
    label: string,
    isVisible: boolean
  ) => {
    if (!isVisible) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LockIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.disabled">
              {label} - Private
            </Typography>
          </Box>
        </Box>
      );
    }

    if (!url) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Link href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </Link>
      </Box>
    );
  };

  const handleBack = () => {
    navigate(`/studio/${studioId}/members`);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading member details...</Typography>
      </Box>
    );
  }

  if (!member) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Members
        </Button>
        <Typography variant="h6" color="error">
          Member not found
        </Typography>
      </Box>
    );
  }

  const visibility = getFieldVisibility(member);
  const displayName =
    member.display_name ||
    member.user?.full_name ||
    member.email ||
    'Pending Invitation';
  const firstName = member.user?.first_name || '';
  const lastName = member.user?.last_name || '';
  const avatarSrc = member.avatar_url || member.user?.avatar_url || undefined;
  const email =
    member.display_email || member.user?.email || member.email || '';

  // Get profile data from backend response
  const profile = (member as any)?.profile;
  const title = profile?.title || '';
  const company = profile?.company || '';
  const bio = profile?.bio || '';
  const phone = (member.user as any)?.phone_number || '';
  const website = profile?.website || '';
  const linkedin = profile?.linkedin || '';
  const instagram = profile?.instagram || '';
  const facebook = profile?.facebook || '';

  // Get address data from backend response respecting privacy settings
  const addressData =
    visibility.showAddress && profile?.address
      ? {
          street_address: profile.address.street_address || '',
          street_address_2: profile.address.street_address_2 || '',
          city: profile.address.city || '',
          state: profile.address.state || '',
          postal_code: profile.address.postal_code || '',
          country: profile.address.country || '',
        }
      : null;

  return (
    <>
      {/* Main App Header/Navigation */}
      <Header />

      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back to Members
          </Button>
          <Typography variant="h4" component="h1">
            Member Details
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Left Column - Profile Overview */}
          <Box sx={{ flex: { xs: '1', md: '0 0 33%' } }}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Avatar
                src={avatarSrc}
                alt={displayName}
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: getRoleColor(member.role),
                  fontSize: '2rem',
                }}
              >
                {!avatarSrc && firstName && lastName
                  ? `${firstName.charAt(0)}${lastName.charAt(0)}`
                  : '?'}
              </Avatar>

              <Typography variant="h5" gutterBottom>
                {displayName}
              </Typography>

              {title && (
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  gutterBottom
                >
                  {title}
                </Typography>
              )}

              {company && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {company}
                </Typography>
              )}

              {/* Role and Status */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Chip
                  icon={getRoleIcon(member.role)}
                  label={
                    member.role
                      ? member.role.charAt(0).toUpperCase() +
                        member.role.slice(1)
                      : 'Unknown'
                  }
                  sx={{
                    bgcolor: `${getRoleColor(member.role)}15`,
                    color: getRoleColor(member.role),
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={member.status}
                  color={getStatusColor(member.status)}
                  variant="outlined"
                />
              </Box>

              {/* Bio */}
              {bio && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    About
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {bio}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Right Column - Detailed Information */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Professional Information */}
              {(title || company) && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Professional Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {title && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <WorkIcon color="action" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Title
                        </Typography>
                        <Typography variant="body1">{title}</Typography>
                      </Box>
                    </Box>
                  )}

                  {company && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <BusinessIcon color="action" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Company
                        </Typography>
                        <Typography variant="body1">{company}</Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Contact Information */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {renderPrivateField(
                  email,
                  visibility.showEmail,
                  <EmailIcon color="action" />,
                  'Email'
                )}

                {renderPrivateField(
                  phone,
                  visibility.showPhone,
                  <PhoneIcon color="action" />,
                  'Phone'
                )}

                {/* Address Information with detailed display */}
                {visibility.showAddress && addressData && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <LocationIcon color="action" sx={{ mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {addressData.street_address}
                      </Typography>
                      {addressData.street_address_2 && (
                        <Typography variant="body1">
                          {addressData.street_address_2}
                        </Typography>
                      )}
                      <Typography variant="body1">
                        {addressData.city}, {addressData.state}{' '}
                        {addressData.postal_code}
                      </Typography>
                      <Typography variant="body1">
                        {addressData.country}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!visibility.showAddress && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <LocationIcon color="action" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <LockIcon fontSize="small" color="disabled" />
                        <Typography variant="body2" color="text.disabled">
                          Private
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Paper>

              {/* Social Links */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Social Links
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {renderSocialLink(
                  website,
                  <WebsiteIcon color="action" />,
                  'Website',
                  visibility.showSocialLinks
                )}

                {renderSocialLink(
                  linkedin,
                  <LinkedInIcon color="action" />,
                  'LinkedIn',
                  visibility.showSocialLinks
                )}

                {renderSocialLink(
                  instagram,
                  <InstagramIcon color="action" />,
                  'Instagram',
                  visibility.showSocialLinks
                )}

                {renderSocialLink(
                  facebook,
                  <FacebookIcon color="action" />,
                  'Facebook',
                  visibility.showSocialLinks
                )}

                {/* Show message only if no social links are visible */}
                {!website && !linkedin && !instagram && !facebook && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.disabled">
                      No social links provided
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Account Information */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Account Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <CalendarIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Joined Studio
                    </Typography>
                    <Typography variant="body1">
                      {formatLongDate(member.joined_at, i18n.language)}
                    </Typography>
                  </Box>
                </Box>

                {member.activated_at && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <VerifiedIcon color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Account Activated
                      </Typography>
                      <Typography variant="body1">
                        {formatLongDate(member.activated_at, i18n.language)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {member.user?.last_login && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <CalendarIcon color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Last Login
                      </Typography>
                      <Typography variant="body1">
                        {formatLongDate(member.user.last_login, i18n.language)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MemberDetailPage;
